import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import Pagination from "../../../components/common/Pagination";
import { getHotelOrders } from "../../../api/user/hotels";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "../../../hooks/useTranslation";

const HotelOrdersTable = () => {
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const limit = 10;
    const navigate = useNavigate();
    const location = useLocation();
    const { bookingId } = location.state || {};
    const { t } = useTranslation();

    const {
        data,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ["hotel-orders", bookingId, page, statusFilter],
        queryFn: () => getHotelOrders(bookingId, page, limit, statusFilter),
        enabled: true,
        keepPreviousData: true,
    });

    const orders = data?.data?.orders || [];
    const pagination = data?.pagination || {};

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case "pending":
            case "placed":
                return "bg-yellow-100 text-yellow-800 border-yellow-300";
            case "confirmed":
                return "bg-green-100 text-green-800 border-green-300";
            case "preparing":
                return "bg-blue-100 text-blue-800 border-blue-300";
            case "ready":
                return "bg-purple-100 text-purple-800 border-purple-300";
            case "served":
                return "bg-teal-100 text-teal-800 border-teal-300";
            case "cancelled":
                return "bg-red-100 text-red-800 border-red-300";
            case "completed":
                return "bg-gray-100 text-gray-800 border-gray-300";
            default:
                return "bg-gray-100 text-gray-800 border-gray-300";
        }
    };

    const getPaymentStatusColor = (status) => {
        const statusLower = status?.toLowerCase();
        switch (statusLower) {
            case "paid":
                return "bg-green-100 text-green-800";
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            case "failed":
                return "bg-red-100 text-red-800";
            case "refunded":
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const statusOptions = [
        { value: "", label: t("hotelOrders").allOrders },
        { value: "pending", label: t("hotelOrders").statusLabels.pending },
        { value: "placed", label: t("hotelOrders").statusLabels.placed },
        { value: "confirmed", label: t("hotelOrders").statusLabels.confirmed },
        { value: "preparing", label: t("hotelOrders").statusLabels.preparing },
        { value: "ready", label: t("hotelOrders").statusLabels.ready },
        { value: "served", label: t("hotelOrders").statusLabels.served },
        { value: "cancelled", label: t("hotelOrders").statusLabels.cancelled },
        { value: "completed", label: t("hotelOrders").statusLabels.completed },
    ];

    const getStatusLabel = (status) => {
        if (!status) return t("hotelOrders").unknown;
        const statusLower = status.toLowerCase();
        const statusLabels = t("hotelOrders")?.statusLabels || {};
        return statusLabels[statusLower] || status || t("hotelOrders").unknown;
    };

    const getPaymentStatusLabel = (status) => {
        if (!status) return t("hotelOrders").unknown;
        const statusLower = status.toLowerCase();
        const paymentStatusLabels = t("hotelOrders")?.paymentStatusLabels || {};
        return paymentStatusLabels[statusLower] || status || t("hotelOrders").unknown;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 mt-5">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <IoIosArrowBack className="w-6 h-6 text-gray-700" />
                        </button>
                        <h1 className="text-3xl font-bold text-gray-900 ">{t("hotelOrders").title}</h1>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <label className="text-sm font-medium text-gray-700">{t("hotelOrders").filterByStatus}</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {statusFilter && (
                            <button
                                onClick={() => {
                                    setStatusFilter("");
                                    setPage(1);
                                }}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                            >
                                {t("hotelOrders").clearFilter}
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="flex justify-center items-center py-12">
                        <div className="w-12 h-12 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                )}

                {/* Error State */}
                {isError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <p className="text-red-600">{error?.message || t("hotelOrders").somethingWentWrong}</p>
                                    </div>
                )}

                {/* Empty State */}
                {!isLoading && !isError && orders.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className="text-gray-600 text-lg">{t("hotelOrders").noOrdersFound}</p>
                        {statusFilter && (
                            <p className="text-gray-500 text-sm mt-2">{t("hotelOrders").tryChangingFilter}</p>
                        )}
                    </div>
                )}

                {/* Orders List */}
                {!isLoading && !isError && orders.length > 0 && (
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <div
                                key={order._id}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
                            >
                                <div className="p-6">
                                    {/* Order Header */}
                                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4 pb-4 border-b border-gray-200">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {t("hotelOrders").order} #{order?.orderId || order?._id?.slice(-8)}
                                                </h3>
                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                                        order?.orderStatus
                                                    )}`}
                                                >
                                                    {getStatusLabel(order?.orderStatus)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {order?.hotelName || order?.bookingId?.hotelId?.name || order?.hotelId?.name || t("hotelOrders").hotelName}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {moment(order?.createdAt).format("MMMM DD, YYYY [at] hh:mm A")}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-gray-900">₾{order?.totalAmount?.toFixed(2) || "0.00"}</p>
                                            <span
                                                className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${getPaymentStatusColor(
                                                    order?.paymentStatus
                                                )}`}
                                            >
                                                {getPaymentStatusLabel(order?.paymentStatus)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Order Details */}
                                    <div className="grid md:grid-cols-2 gap-6">
                                        {/* Left Column */}
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("hotelOrders").roomInformation}</h4>
                                                <div className="space-y-1 text-sm text-gray-600">
                                                    <p>
                                                        <span className="font-medium">{t("hotelOrders").room}:</span>{" "}
                                                        {order?.roomNumbers || order?.roomId?.roomNumber || t("hotelOrders").nA}
                                                    </p>
                                                    {order?.deliveryMethod && (
                                                        <p>
                                                            <span className="font-medium">{t("hotelOrders").delivery}:</span> {order.deliveryMethod}
                                                        </p>
                                                    )}
                                                    {order?.deliveryTime && (
                                                        <p>
                                                            <span className="font-medium">{t("hotelOrders").deliveryTime}:</span> {order.deliveryTime}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("hotelOrders").itemsOrdered}</h4>
                                                <div className="space-y-1">
                                                    {order?.items?.map((item, idx) => (
                                                        <div key={idx} className="text-sm text-gray-600 flex justify-between">
                                                            <span>
                                                                {item.quantity} x {item.itemId?.itemName || t("hotelOrders").unknownItem}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("hotelOrders").paymentDetails}</h4>
                                                <div className="space-y-1 text-sm text-gray-600">
                                                    <p>
                                                        <span className="font-medium">{t("hotelOrders").method}:</span> {order?.paymentMethod || t("hotelOrders").nA}
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">{t("hotelOrders").subtotal}:</span> ₾{order?.subtotal?.toFixed(2) || "0.00"}
                                                    </p>
                                                    {order?.discountAmount > 0 && (
                                                        <p>
                                                            <span className="font-medium">{t("hotelOrders").discount}:</span> -₾{order.discountAmount.toFixed(2)}
                                                        </p>
                                                    )}
                                                    {order?.platformFee > 0 && (
                                                        <p>
                                                            <span className="font-medium">{t("hotelOrders").platformFee}:</span> ₾{order.platformFee.toFixed(2)}
                                                        </p>
                                                    )}
                                                    {order?.tokenCharges > 0 && (
                                                        <p>
                                                            <span className="font-medium">{t("hotelOrders").tokenCharges}:</span> ₾{order.tokenCharges.toFixed(2)}
                                                        </p>
                                                    )}
                                                    <p className="pt-2 border-t border-gray-200">
                                                        <span className="font-semibold text-gray-900">{t("hotelOrders").total}:</span>{" "}
                                                        <span className="font-bold text-lg text-gray-900">₾{order?.totalAmount?.toFixed(2) || "0.00"}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            {order?.notes && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">{t("hotelOrders").notes}</h4>
                                                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{order.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {!isLoading && !isError && pagination?.totalPages > 1 && (
                    <div className="mt-6">
                <Pagination
                            page={pagination.page || pagination.currentPage}
                    totalPages={pagination.totalPages}
                    setPage={setPage}
                />
                    </div>
            )}
            </div>
        </div>
    );
};

export default HotelOrdersTable;
